import { ArrowsClockwiseIcon, GiftIcon, type Icon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { type BillingInterval, openManage } from '~/session/commerce.js';
import { useAuthStore } from '~/store/authStore.js';
import { ActionMenu, type ActionMenuItem } from '~/ui/ActionMenu.js';
import { Button, ButtonLink } from '~/ui/Button.js';
import { cx } from '~/ui/cx.js';
import { FieldLabel } from '~/ui/FieldLabel.js';
import { Section } from '~/ui/Section.js';
import { useArrangement } from '~/ui/useArrangement.js';
import {
  CALL_FAILED,
  FOCUS,
  HEADLINE,
  SUBSCRIPTION_PATH,
  type BilledSubscriptionView,
  type Standing,
  type SubscriptionState,
  billedCents,
  billedOf,
  intervalOf,
  isMember,
  longDate,
  onward,
  priceParts,
  signInFirst,
  standingOf,
  storeOf,
  subscriptionOf,
  subscriptionPath,
  useBillingStore,
  useCheckoutReturn,
  useStoreCall,
  useSubscriptionPreview,
} from './subscriptionPages.js';

/**
 * The Subscription group: where a player stands, the way to manage it, and
 * restore.
 *
 * Drawn to the `lobby-subscription-settings` sketch (sections B and E) and
 * `docs/ux/account/settings.md`, which owes three things: status, manage,
 * restore. The headline is the plan's name for a member and Playing free for
 * everyone else, then one sentence for the state. A member's date sits in a
 * well with its billing hint. A free player, a guest and an ended
 * subscription get See the offer, the one lemon.
 *
 * Billing stays with the store the plan was bought in (D28), so Manage leaves
 * the app, and every line that names a store reads it off `/me`. A device
 * that is not that store cannot manage it: the chip goes, and the well says
 * where to go instead.
 *
 * Friends & Family is a comp an operator grants (Mitch, 2026-10-09). No store
 * bills it, so it shows no price and no Manage, only a well saying it is
 * complimentary and when it ends, if it does.
 */
export function SubscriptionPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const standing = standingOf(user);
  const subscription = subscriptionOf(user);
  const member = isMember(standing) && subscription !== null;
  const billed = subscription === null ? null : billedOf(subscription);
  const guest = standing === 'guest';
  const unconfirmed = useCheckoutReturn();

  return (
    <>
      <Section title={t('subscription.title')}>
        {member ? (
          // A subscriber's headline is their plan's name, not a status word (Mitch, 2026-09-21).
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className={HEADLINE}>{t(`subscription.plans.${subscription.plan}`)}</p>
              <p className="mt-2 text-muted">{t(`subscription.plans.${subscription.plan}Line`)}</p>
            </div>
            {billed !== null && <Price cents={billedCents(billed)} interval={intervalOf(billed)} />}
          </div>
        ) : (
          <p className={HEADLINE}>{t('subscription.statusFree')}</p>
        )}
        {unconfirmed && (
          <Outcome text={t('subscription.result.checkout.pending')} className="mt-2" />
        )}
        {member && subscription.pending_change != null && (
          <p className="mt-2 text-ink">
            {t('subscription.changing', {
              plan: t(`subscription.plans.${subscription.pending_change.plan}`),
              date: longDate(subscription.pending_change.starts_at, i18n.language),
            })}
          </p>
        )}
        {!member && (
          <p className="mt-2 text-muted">
            {standing === 'ended'
              ? t('subscription.endedLine', {
                  date: longDate(subscription?.ended_at, i18n.language),
                })
              : t('subscription.valueLine')}
          </p>
        )}
        {guest && <p className="mt-2 text-muted">{t('subscription.guestLine')}</p>}
        {!member && (
          <div className="mt-4 flex flex-col gap-3">
            <ButtonLink
              variant="primary"
              to={subscriptionPath('offer')}
              data-focus-key={FOCUS.offer}
            >
              {t('subscription.seeOffer')}
            </ButtonLink>
            {guest && (
              // Signed in, the guest comes back here (`docs/ux/commerce/purchase.md`).
              <ButtonLink {...signInFirst(SUBSCRIPTION_PATH)}>{t('auth.signIn')}</ButtonLink>
            )}
          </div>
        )}
        {member &&
          (billed === null ? (
            <ComplimentaryWell endsAt={subscription.current_period_ends_at} />
          ) : (
            <MemberRows standing={standing} subscription={billed} />
          ))}
      </Section>
      <PurchasesBand guest={guest} from="subscription" />
    </>
  );
}

type ManageOption = 'plan' | 'resume' | 'payment' | 'cancel';

/**
 * A member's date in its well, then the one chip that closes the card at its
 * bottom right (Mitch, 2026-09-21): Manage, or Update when a payment failed.
 */
function MemberRows({
  standing,
  subscription,
}: {
  readonly standing: Standing;
  readonly subscription: BilledSubscriptionView;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const preview = useSubscriptionPreview();
  const phone = useArrangement() === 'phone';
  const device = useBillingStore();
  const bought = storeOf(subscription);
  // Only the store that bills it can change it (D28).
  const reachable = bought === device;
  const store = t(`subscription.store.${bought}`);
  const elsewhere = t(`subscription.elsewhere_${bought}`);
  const paidThrough = longDate(subscription.current_period_ends_at, i18n.language);
  const manage = useStoreCall();
  const { outcome } = manage;

  // A second press while the store is out does nothing, and the chip keeps focus.
  const leave = (): void => {
    if (manage.busy) return;
    manage.run(openManage, CALL_FAILED, async (result, ours) => {
      // Verified here means the store changed something: read `/me` for it.
      if (result.status === 'verified') await useAuthStore.getState().refresh();
      if (ours()) manage.show(result.status);
    });
  };

  const options: readonly ManageOption[] =
    standing === 'ending' ? ['resume', 'payment'] : ['plan', 'payment', 'cancel'];
  const items: readonly ActionMenuItem<ManageOption>[] = options.map((option) => ({
    value: option,
    label: t(`subscription.manageMenu.options.${option}`),
    // Change plan stays in the app; the rest leave for the store and say where.
    hint: option === 'plan' ? undefined : t(`subscription.manageMenu.where.${bought}`),
    leaves: option !== 'plan',
  }));

  return (
    <>
      {standing === 'renewing' && (
        <MemberWell
          renewing
          label={t('subscription.renews', { date: paidThrough })}
          hint={reachable ? t('subscription.billed', { store }) : elsewhere}
        />
      )}
      {standing === 'ending' && (
        <MemberWell
          label={t('subscription.ends', { date: paidThrough })}
          hint={reachable ? t('subscription.endingHint') : elsewhere}
        />
      )}
      {standing === 'unpaid' && (
        <MemberWell
          label={t('subscription.paymentFailed')}
          hint={t('subscription.benefitsStay', {
            date: longDate(subscription.grace_ends_at, i18n.language),
          })}
        />
      )}
      {standing === 'unpaid' && !reachable && <p className="mt-4 text-muted">{elsewhere}</p>}
      {reachable && standing === 'unpaid' && (
        <div className="mt-4 flex justify-end">
          <Button
            variant="primary"
            size="sm"
            aria-disabled={manage.busy ? true : undefined}
            onClick={leave}
          >
            {t('subscription.update')}
          </Button>
        </div>
      )}
      {reachable && standing !== 'unpaid' && (
        // The wrapper wears the focus key: the chip inside is `ActionMenu`'s own.
        <div className="mt-4 flex justify-end" data-focus-key={FOCUS.manage}>
          <ActionMenu
            label={t('subscription.manage')}
            title={t('subscription.manageMenu.title')}
            closeLabel={t('common.close')}
            items={items}
            layout={phone ? 'sheet' : 'dropdown'}
            defaultOpen={preview.menuOpen === true}
            onPick={(option) => {
              if (option === 'plan') {
                void navigate(subscriptionPath('change-plan'));
              } else {
                leave();
              }
            }}
          />
        </div>
      )}
      <Outcome
        className={cx((outcome !== null || manage.busy) && 'mt-3')}
        busy={manage.busy}
        text={outcome === null ? null : t(`subscription.result.manage.${outcome}`)}
      />
    </>
  );
}

/** Friends & Family: complimentary, until a date or for as long as it is granted. */
function ComplimentaryWell({ endsAt }: { readonly endsAt: string | null }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  return (
    <MemberWell
      icon={GiftIcon}
      label={
        endsAt === null
          ? t('subscription.complimentary')
          : t('subscription.complimentaryUntil', { date: longDate(endsAt, i18n.language) })
      }
      hint={t('subscription.complimentaryHint')}
    />
  );
}

/**
 * The member's date and its hint, in the well the emote gate uses in the
 * table talk tray (`table/talk/TalkTray.tsx`): `ground` inset in the white
 * card. A renewing member's well carries an icon, and so does a comp's, which
 * asks nothing of the player either. Renewal off and payment failed ask the
 * player to act, so they wear `red-muted` with no icon (Mitch, 2026-09-21).
 */
function MemberWell({
  renewing = false,
  icon,
  label,
  hint,
}: {
  readonly renewing?: boolean;
  /** A calm well's own icon, in place of a renewing well's arrows. */
  readonly icon?: Icon;
  readonly label: string;
  readonly hint: string;
}): React.JSX.Element {
  const WellIcon = icon ?? (renewing ? ArrowsClockwiseIcon : null);
  return (
    <div
      className={cx(
        'mt-4 flex items-center gap-3 rounded-xl p-4',
        WellIcon === null ? 'bg-red-muted' : 'bg-ground',
      )}
    >
      {WellIcon !== null && (
        <WellIcon size={20} className="shrink-0 text-muted" aria-hidden="true" />
      )}
      <FieldLabel hint={hint} className="flex-1">
        {label}
      </FieldLabel>
    </div>
  );
}

/**
 * The restore footer: one hint and a ghost Restore purchases door, with no
 * panel or heading. It closes both the Subscription page and the offer,
 * because a player who lands on either without a subscription may already
 * have one on another device (D15). A guest is told to sign in first,
 * because restore checks the account (`docs/ux/commerce/restore.md`).
 */
export function PurchasesBand({
  guest,
  from,
  state,
}: {
  readonly guest: boolean;
  /** Which page this band closes, so restore's Back lands there again. */
  readonly from: 'offer' | 'subscription';
  /** The offer's own state, whose way back out restore carries on. */
  readonly state?: SubscriptionState | undefined;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center">
      <p className="text-center text-muted">
        {guest ? t('subscription.restoreGuestHint') : t('subscription.restoreHint')}
      </p>
      <ButtonLink
        variant="ghost"
        to={subscriptionPath('restore')}
        state={
          from === 'offer' && state !== undefined ? { restoreFrom: 'offer', ...onward(state) } : {}
        }
        data-focus-key={FOCUS.restore}
      >
        {t('subscription.restoreDoor')}
      </ButtonLink>
    </div>
  );
}

/**
 * The line a store action leaves under its controls, in a status region so it
 * is heard as well as seen. While the store is out it says so, because the
 * control that was pressed only refuses a second press, and does not change.
 */
export function Outcome({
  text,
  busy = false,
  className,
}: {
  readonly text: string | null;
  readonly busy?: boolean;
  /** Layout only: alignment and the space above the line. */
  readonly className?: string | false | undefined;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <p role="status" className={cx('text-ink', className)}>
      {busy ? t('common.working') : text}
    </p>
  );
}

/**
 * A price in the display face, the dollars big and the cents a step smaller
 * and lifted, the way a shelf tag writes 99 cents. The period sits under it,
 * small and muted, right-aligned with the figure: beside it, it was the word
 * that pushed the price off a phone's right edge (Mitch, 2026-09-21).
 */
export function Price({
  cents,
  interval,
}: {
  readonly cents: number;
  readonly interval: BillingInterval;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { whole, fraction } = priceParts(cents);
  return (
    <span className="flex flex-col items-end gap-1 whitespace-nowrap">
      <span className="font-display text-2xl leading-none font-extrabold tracking-tight">
        {whole}
        <span className="align-top text-base">{fraction}</span>
      </span>
      <span className="text-xs text-muted">{t(`subscription.plans.per_${interval}`)}</span>
    </span>
  );
}
